import { useState, useCallback, useRef, useEffect } from 'react';

interface UseBackendSearchOptions<T> {
  searchFn: (query: string, limit: number, signal?: AbortSignal) => Promise<T[]>;
  limit?: number;
  debounceMs?: number;
}

interface UseBackendSearchReturn<T> {
  results: T[];
  isSearching: boolean;
  hasLoadedInitial: boolean;
  handleSearch: (query: string) => void;
  loadInitialData: () => void;
}

/**
 * Hook for managing backend-searched dropdowns.
 * Provides debounced search, initial data loading, and request cancellation.
 */
export function useBackendSearch<T>({
  searchFn,
  limit = 50,
  debounceMs = 300,
}: UseBackendSearchOptions<T>): UseBackendSearchReturn<T> {
  const [results, setResults] = useState<T[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const loadInitialData = useCallback(async () => {
    if (hasLoadedInitial || results.length > 0) return;

    setIsSearching(true);
    abortControllerRef.current = new AbortController();

    try {
      const data = await searchFn('', limit, abortControllerRef.current.signal);
      setResults(data);
      setHasLoadedInitial(true);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Initial load error:', error);
      }
    } finally {
      setIsSearching(false);
    }
  }, [hasLoadedInitial, results.length, searchFn, limit]);

  const handleSearch = useCallback(
    (query: string) => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Abort previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // If query is empty, load initial data
      if (!query.trim()) {
        loadInitialData();
        return;
      }

      // Debounce the search
      debounceTimerRef.current = setTimeout(async () => {
        setIsSearching(true);
        abortControllerRef.current = new AbortController();

        try {
          const data = await searchFn(query.trim(), limit, abortControllerRef.current.signal);
          setResults(data);
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Search error:', error);
            setResults([]);
          }
        } finally {
          setIsSearching(false);
        }
      }, debounceMs);
    },
    [loadInitialData, searchFn, limit, debounceMs]
  );

  return {
    results,
    isSearching,
    hasLoadedInitial,
    handleSearch,
    loadInitialData,
  };
}

